import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useDiscoveryHost } from "@/components/discovery-host-context";
import { BusinessOnboardingHostContext } from "./onboarding-host-context";
import { resetJourneyForAdmin } from "./storage";
import { useOnboardingConfiguration } from "./configuration";
import { useManagedCompanies } from "@/auth/useManagedCompanies";
import { publicOnboardingAllowed } from "./eligibility";
import "./business-onboarding.css";

const BusinessOnboarding = lazy(() =>
  import("./BusinessOnboarding").then((module) => ({ default: module.BusinessOnboarding })),
);

/** Shared public launch boundary; the route and overlay use the same journey. */
export function BusinessOnboardingHostProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<string | null>(null);
  const activeRef = useRef<string | null>(null);
  const submissionPending = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const onSubmissionPendingChange = useCallback((pending: boolean) => {
    submissionPending.current = pending;
    setSubmitting(pending);
  }, []);
  const adminTest = useRef(false);
  const account = useManagedCompanies();
  const canOpenOnboarding = publicOnboardingAllowed(account);
  const pendingEligibility = account.resolving ||
    (!account.adminError && !account.membershipError && account.companies === null);
  const eligibility = useRef({ canOpenOnboarding, account });
  eligibility.current = { canOpenOnboarding, account };
  const dialog = useRef<HTMLDivElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const { isDiscoveryOpen, onboardingRequest = null } = useDiscoveryHost();
  const lastDiscoveryRequest = useRef(onboardingRequest);
  const closeOnboarding = useCallback(() => {
    if (submissionPending.current) return;
    activeRef.current = null;
    adminTest.current = false;
    setActive(null);
  }, []);
  const openOnboarding = useCallback((options?: { freshForAdmin?: boolean }) => {
    if (window.parent !== window || submissionPending.current) return;
    const latest = eligibility.current;
    if (options?.freshForAdmin
      ? !latest.account.isSystemAdmin || latest.account.resolving || latest.account.adminError
      : !latest.canOpenOnboarding) return;
    if (activeRef.current && !options?.freshForAdmin) return;
    void useOnboardingConfiguration.getState().load();
    returnFocus.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (options?.freshForAdmin) resetJourneyForAdmin();
    adminTest.current = Boolean(options?.freshForAdmin);
    activeRef.current = crypto.randomUUID();
    setActive(activeRef.current);
  }, []);
  const api = useMemo(
    () => ({ openOnboarding, closeOnboarding, canOpenOnboarding, isOnboardingOpen: Boolean(active) }),
    [openOnboarding, closeOnboarding, canOpenOnboarding, active],
  );

  useEffect(() => {
    if (onboardingRequest === lastDiscoveryRequest.current) return;
    lastDiscoveryRequest.current = onboardingRequest;
    const wasOpen = Boolean(activeRef.current);
    openOnboarding();
    if (!wasOpen && activeRef.current && onboardingRequest) returnFocus.current = onboardingRequest.returnFocus;
  }, [onboardingRequest, openOnboarding]);

  useEffect(() => {
    if (active && !pendingEligibility &&
      (adminTest.current ? !account.isSystemAdmin || Boolean(account.adminError) : !canOpenOnboarding)) {
      submissionPending.current = false;
      setSubmitting(false);
      closeOnboarding();
    }
  }, [active, pendingEligibility, canOpenOnboarding, account.isSystemAdmin, account.adminError, closeOnboarding]);

  useEffect(() => {
    if (!active) {
      const target = returnFocus.current;
      returnFocus.current = null;
      if (target?.isConnected) window.requestAnimationFrame(() => target.focus());
      return;
    }
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current?.focus();
    return () => {
      document.body.style.overflow = previous;
    };
  }, [active]);

  useEffect(() => {
    // Discovery owns keyboard/focus while its preview is above onboarding.
    if (!active || isDiscoveryOpen) return;
    const focusable = () =>
      Array.from(
        dialog.current?.querySelectorAll<HTMLElement>(
          "a[href],button,input,select,textarea,[tabindex]",
        ) ?? [],
      ).filter(
        (element) =>
          element.tabIndex >= 0 &&
          !element.matches(":disabled") &&
          element.getClientRects().length > 0,
      );
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeOnboarding();
      } else if (event.key === "Tab") {
        const items = focusable();
        const first = items[0],
          last = items.at(-1);
        if (
          !first ||
          (event.shiftKey
            ? document.activeElement === first || document.activeElement === dialog.current
            : document.activeElement === last || document.activeElement === dialog.current)
        ) {
          event.preventDefault();
          (event.shiftKey ? last : first)?.focus();
        }
      }
    };
    const containFocus = (event: FocusEvent) => {
      if (!dialog.current?.contains(event.target as Node)) dialog.current?.focus();
    };
    window.addEventListener("keydown", keydown);
    document.addEventListener("focusin", containFocus);
    return () => {
      window.removeEventListener("keydown", keydown);
      document.removeEventListener("focusin", containFocus);
    };
  }, [active, isDiscoveryOpen, closeOnboarding]);

  const activeEligible = canOpenOnboarding && (!adminTest.current || account.isSystemAdmin);
  return (
    <BusinessOnboardingHostContext.Provider value={api}>
      <div inert={active ? true : undefined} aria-hidden={active ? true : undefined}>
        {children}
      </div>
      {active &&
        createPortal(
          <div
            data-testid="business-onboarding-overlay"
            className="fixed inset-0 z-[90] grid place-items-center bg-foreground/20 p-0 backdrop-blur-[3px] sm:p-6"
            inert={isDiscoveryOpen ? true : undefined}
            aria-hidden={isDiscoveryOpen ? true : undefined}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeOnboarding();
            }}
          >
            <div
              ref={dialog}
              role="dialog"
              aria-modal="true"
              aria-label="Qotta Business Onboarding"
              tabIndex={-1}
              className="business-onboarding-dialog animate-scale-in relative flex h-[100dvh] w-full flex-col overflow-hidden bg-surface shadow-[0_28px_90px_-24px_var(--shadow-color-strong)] outline-none sm:w-[min(1100px,92vw)] sm:rounded-[28px] sm:border sm:border-border/80"
            >
              <div className="absolute right-2 top-2 z-10 sm:right-3 sm:top-3">
                <button
                  type="button"
                  onClick={closeOnboarding}
                  disabled={submitting}
                  aria-label="Close business onboarding"
                  className="flex size-11 items-center justify-center rounded-xl text-muted-foreground hover:bg-primary-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                >
                  <X aria-hidden className="size-5" />
                </button>
              </div>
              <div
                className="business-onboarding-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain"
                data-testid="business-onboarding-scroll"
                inert={!activeEligible ? true : undefined}
                aria-hidden={!activeEligible ? true : undefined}
              >
                <Suspense
                  fallback={
                    <p role="status" className="p-8">
                      Loading your journey...
                    </p>
                  }
                >
                  {(activeEligible || pendingEligibility) &&
                    <BusinessOnboarding key={active} onClose={closeOnboarding} onSubmissionPendingChange={onSubmissionPendingChange} />}
                </Suspense>
              </div>
              {pendingEligibility && <div className="absolute inset-14 grid place-items-center bg-surface" role="status">Checking account...</div>}
            </div>
          </div>,
          document.body,
        )}
    </BusinessOnboardingHostContext.Provider>
  );
}
